import React, { useEffect, useState, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import i18n from '@/i18n'
import { fetchGeoLocale, getCachedGeoLocale } from '@/lib/geo-locale'
import { useGeoLocaleStore } from '@/lib/geo-locale-store'
import { OnboardingProvider } from '@/contexts/OnboardingContext'
import { PermissionsProvider } from '@/contexts/PermissionsContext'
import { LocaleProvider } from '@/contexts/LocaleContext'
import { MaintenancePage } from '@/components/maintenance/MaintenancePage'
import { ConsentProvider } from '@/components/privacy/ConsentProvider'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { AuthorizedRoute } from '@/components/auth/AuthorizedRoute'
import { LoadingState } from '@/components/primitives'
import { resolveRouteLocale } from '@/lib/route-locale'
import { LoginPage } from '@/components/auth/LoginPage'
import { RegisterPage } from '@/components/auth/RegisterPage'
import { ForgotPasswordPage } from '@/components/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/components/auth/ResetPasswordPage'
import { EmailVerifiedPage } from '@/components/auth/EmailVerifiedPage'
import { EmailVerifyFailedPage } from '@/components/auth/EmailVerifyFailedPage'
import { HotmartWelcomePage } from '@/components/auth/HotmartWelcomePage'
import { LandingPage } from '@/components/landing'
import { SolutionPage } from '@/components/landing/solutions'
import { CookiePolicyPage, PrivacyPage, TermsPage } from '@/components/legal'
import { AppShell } from '@/components/layout/AppShell'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import { useUpgradeSuccess } from '@/hooks/useUpgradeSuccess'
import { OnboardingModal } from '@/components/onboarding'
import { useAnalytics } from '@/lib/analytics/usePageTracking'
import { getPreferredMaintenanceLocale, isMaintenanceModeEnabled } from '@/lib/maintenance-mode'
import {
  DashboardSkeleton,
  ProposalsSkeleton,
  ProposalDetailsSkeleton,
  PackagesSkeleton,
  ContractTemplatesSkeleton,
  AddOnsSkeleton,
  EarningsSkeleton,
  MetricsSkeleton,
  PreferencesSkeleton,
  BillingSkeleton,
  PublicPaymentSkeleton,
  PageSkeleton,
} from '@/components/skeletons'

// Lazy-loaded pages (route-level code-splitting)
const Dashboard = lazy(() => import('@/components/Dashboard').then(m => ({ default: m.Dashboard })))
const PreferencesPage = lazy(() => import('@/components/PreferencesPage').then(m => ({ default: m.PreferencesPage })))
const PackagesPage = lazy(() => import('@/components/PackagesPage.tsx').then(m => ({ default: m.PackagesPage })))
const ContractTemplatesPage = lazy(() => import('@/components/ContractTemplatesPage').then(m => ({ default: m.ContractTemplatesPage })))
const AddOnsPage = lazy(() => import('@/components/AddOnsPage').then(m => ({ default: m.AddOnsPage })))
const ProposalsPage = lazy(() => import('@/components/ProposalsPage').then(m => ({ default: m.ProposalsPage })))
const FollowUpsPage = lazy(() => import('@/components/FollowUpsPage').then(m => ({ default: m.FollowUpsPage })))
const BookingRequestsInbox = lazy(() => import('@/components/booking-requests/BookingRequestsInbox').then(m => ({ default: m.BookingRequestsInbox })))
const ProposalDetailsPage = lazy(() => import('@/components/ProposalDetailsPage').then(m => ({ default: m.ProposalDetailsPage })))
const MetricsPage = lazy(() => import('@/components/MetricsPage').then(m => ({ default: m.MetricsPage })))
const EarningsPage = lazy(() => import('@/components/EarningsPage').then(m => ({ default: m.EarningsPage })))
const BillingPage = lazy(() => import('@/components/BillingPage').then(m => ({ default: m.BillingPage })))
const IntegrationsDocsPage = lazy(() => import('@/components/IntegrationsDocsPage').then(m => ({ default: m.IntegrationsDocsPage })))
const GoogleCallbackPage = lazy(() => import('@/components/GoogleCallbackPage').then(m => ({ default: m.GoogleCallbackPage })))
const OutlookCallbackPage = lazy(() => import('@/components/OutlookCallbackPage').then(m => ({ default: m.OutlookCallbackPage })))
const StripeCallbackPage = lazy(() => import('@/components/StripeCallbackPage').then(m => ({ default: m.StripeCallbackPage })))
const MercadoPagoCallbackPage = lazy(() => import('@/components/MercadoPagoCallbackPage').then(m => ({ default: m.MercadoPagoCallbackPage })))
const PublicPaymentPage = lazy(() => import('@/components/PublicPaymentPage').then(m => ({ default: m.PublicPaymentPage })))
const PublicBookingPage = lazy(() => import('@/components/public-page/PublicBookingPage').then(m => ({ default: m.PublicBookingPage })))
const BalancePaymentPage = lazy(() => import('@/components/BalancePaymentPage').then(m => ({ default: m.BalancePaymentPage })))
const PaymentSuccessPage = lazy(() => import('@/components/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })))
const PricingPage = lazy(() => import('@/components/PricingPage').then(m => ({ default: m.PricingPage })))
const AdminPage = lazy(() => import('@/components/admin/AdminPage').then(m => ({ default: m.AdminPage })))
const TeamPage = lazy(() => import('@/components/TeamPage'))
const JoinTeamPage = lazy(() => import('@/components/team/JoinTeamPage').then(m => ({ default: m.JoinTeamPage })))
const BlogPage = lazy(() => import('@/components/blog/BlogPage').then(m => ({ default: m.default })))
const BlogPostPage = lazy(() => import('@/components/blog/BlogPostPage').then(m => ({ default: m.default })))


function GeoLocaleInitializer() {
  const { setGeoLocale, setGeoLoading } = useGeoLocaleStore()

  useEffect(() => {
    fetchGeoLocale().then((result) => {
      setGeoLocale(result)
      setGeoLoading(false)
      if (result && !localStorage.getItem('i18nextLng')) {
        i18n.changeLanguage(result.language)
      }
    })
  }, [])

  return null
}

function App() {
  return (
    <BrowserRouter>
      {isMaintenanceModeEnabled() ? (
        <MaintenanceRoutes />
      ) : (
        <AuthProvider>
          <PermissionsProvider>
            <GeoLocaleInitializer />
            <LocaleProvider>
              <ConsentProvider>
                <AppRoutes />
              </ConsentProvider>
            </LocaleProvider>
          </PermissionsProvider>
        </AuthProvider>
      )}
    </BrowserRouter>
  )
}

function MaintenanceRootRedirect() {
  return <Navigate to={`/${getPreferredMaintenanceLocale()}/maintenance`} replace />
}

function MaintenanceRoutes() {
  return (
    <Routes>
      <Route path="/" element={<MaintenanceRootRedirect />} />
      <Route path="/:locale" element={<MaintenancePage />} />
      <Route path="/:locale/*" element={<MaintenancePage />} />
      <Route path="*" element={<MaintenanceRootRedirect />} />
    </Routes>
  )
}

/**
 * Root redirect: "/" → authenticated user's dashboard, or the landing page,
 * both in the resolved locale. Waits for auth restoration to avoid a
 * landing-page flash before an authenticated redirect to the dashboard.
 */
function RootRedirect() {
  const { isLoading, isAuthenticated, user } = useAuth()
  const geo = getCachedGeoLocale()
  const locale = resolveRouteLocale([user?.locale, geo?.language])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--pd-bg)] flex items-center justify-center">
        <LoadingState type="page" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={`/${locale}/dashboard`} replace />
  }

  return <Navigate to={`/${locale}/`} replace />
}

/**
 * Helper to redirect routes without locale to user's preferred locale
 */
function UserLocaleRedirect({ to }: { to: string }) {
  const { user } = useAuth()
  const location = useLocation()
  const params = useParams()

  // Check for ?lang= query param first
  const searchParams = new URLSearchParams(location.search)
  const langParam = searchParams.get('lang')
  const geo = getCachedGeoLocale()

  // Priority: 1) ?lang= param, 2) user locale, 3) geo/cached locale, 4) default
  const locale = resolveRouteLocale([langParam, user?.locale, geo?.language])

  // Replace params in the 'to' path (e.g., :slug, :token, :id, :publicId)
  let finalPath = to
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      finalPath = finalPath.replace(`:${key}`, value)
    }
  })
  
  // Preserve query params except 'lang'
  searchParams.delete('lang')
  const queryString = searchParams.toString()
  const search = queryString ? `?${queryString}` : ''
  
  return <Navigate to={`/${locale}${finalPath}${search}`} replace />
}

function WelcomeAliasRedirect() {
  const { locale, token } = useParams<{ locale: string; token?: string }>()
  const resolvedLocale = locale || 'en'
  return (
    <Navigate
      to={token ? `/${resolvedLocale}/boas-vindas/${token}` : `/${resolvedLocale}/boas-vindas`}
      replace
    />
  )
}

/**
 * Main routes: public, public-with-locale, app
 */
function AppRoutes() {
  const navigate = useNavigate()
  const location = useLocation()

  // Initialize analytics tracking (page views, scroll, time on page)
  useAnalytics()

  // Extract current page from pathname (remove locale prefix)
  const pathWithoutLocale = location.pathname.replace(/^\/(en|pt|es)/, '')
  const currentPage = (pathWithoutLocale?.slice(1) || '') as 'dashboard' | 'proposals' | 'follow-ups' | 'booking-requests' | 'packages' | 'contract-templates' | 'addons' | 'earnings' | 'metrics' | 'preferences' | 'pricing' | 'billing' | 'recurring-plans' | 'integrations' | 'admin' | 'team'

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Locale-based public routes */}
      <Route path="/:locale/*" element={<LocaleRoutes />} />

      {/* Legacy routes (without locale prefix) - redirect to user's locale */}
      <Route path="/login" element={<UserLocaleRedirect to="/login" />} />
      <Route path="/register" element={<UserLocaleRedirect to="/register" />} />
      <Route path="/forgot-password" element={<UserLocaleRedirect to="/forgot-password" />} />
      <Route path="/reset-password/:token" element={<UserLocaleRedirect to="/reset-password/:token" />} />
      <Route path="/join/:token" element={<UserLocaleRedirect to="/join/:token" />} />
      <Route path="/maintenance" element={<UserLocaleRedirect to="/maintenance" />} />
      <Route path="/manutencao" element={<UserLocaleRedirect to="/maintenance" />} />
      <Route path="/mantenimiento" element={<UserLocaleRedirect to="/maintenance" />} />
      <Route path="/email-verified" element={<EmailVerifiedPage />} />
      <Route path="/email-verify-failed" element={<EmailVerifyFailedPage />} />
      <Route path="/boas-vindas" element={<UserLocaleRedirect to="/boas-vindas" />} />
      <Route path="/boas-vindas/:token" element={<UserLocaleRedirect to="/boas-vindas/:token" />} />
      <Route path="/welcome" element={<UserLocaleRedirect to="/welcome" />} />
      <Route path="/welcome/:token" element={<UserLocaleRedirect to="/welcome/:token" />} />
      <Route path="/solutions/:slug" element={<UserLocaleRedirect to="/solutions/:slug" />} />
      <Route path="/privacy" element={<UserLocaleRedirect to="/privacy" />} />
      <Route path="/privacidade" element={<UserLocaleRedirect to="/privacidade" />} />
      <Route path="/terms" element={<UserLocaleRedirect to="/terms" />} />
      <Route path="/termos" element={<UserLocaleRedirect to="/termos" />} />
      <Route path="/cookies" element={<UserLocaleRedirect to="/cookies" />} />
      <Route path="/politica-de-cookies" element={<UserLocaleRedirect to="/politica-de-cookies" />} />
      <Route path="/pay/:publicId" element={<UserLocaleRedirect to="/pay/:publicId" />} />
      <Route path="/u/:slug" element={<UserLocaleRedirect to="/u/:slug" />} />
      <Route path="/u/:slug/pro/:proSlug" element={<UserLocaleRedirect to="/u/:slug/pro/:proSlug" />} />
      <Route path="/p/:slug/pro/:proSlug" element={<UserLocaleRedirect to="/p/:slug/pro/:proSlug" />} />
      <Route path="/dashboard" element={<UserLocaleRedirect to="/dashboard" />} />
      <Route path="/proposals" element={<UserLocaleRedirect to="/proposals" />} />
      <Route path="/proposals/:id" element={<UserLocaleRedirect to="/proposals/:id" />} />
      <Route path="/follow-ups" element={<UserLocaleRedirect to="/follow-ups" />} />
      <Route path="/booking-requests" element={<UserLocaleRedirect to="/booking-requests" />} />
      <Route path="/packages" element={<UserLocaleRedirect to="/packages" />} />
      <Route path="/contract-templates" element={<UserLocaleRedirect to="/contract-templates" />} />
      <Route path="/addons" element={<UserLocaleRedirect to="/addons" />} />
      <Route path="/earnings" element={<UserLocaleRedirect to="/earnings" />} />
      <Route path="/metrics" element={<UserLocaleRedirect to="/metrics" />} />
      <Route path="/preferences" element={<UserLocaleRedirect to="/preferences" />} />
      <Route path="/pricing" element={<UserLocaleRedirect to="/pricing" />} />
      <Route path="/billing" element={<UserLocaleRedirect to="/billing" />} />
      <Route path="/recurring-plans" element={<UserLocaleRedirect to="/packages" />} />
      <Route path="/integrations" element={<UserLocaleRedirect to="/integrations" />} />
      <Route path="/integrations/docs" element={<UserLocaleRedirect to="/integrations/docs" />} />
      <Route path="/integrations-docs/:id" element={<UserLocaleRedirect to="/integrations-docs/:id" />} />
      <Route path="/admin" element={<UserLocaleRedirect to="/admin" />} />
      <Route path="/team" element={<UserLocaleRedirect to="/team" />} />
      <Route path="/google-callback" element={<UserLocaleRedirect to="/google-callback" />} />
      <Route path="/stripe-callback" element={<UserLocaleRedirect to="/stripe-callback" />} />
      <Route path="/integrations/google/callback" element={<UserLocaleRedirect to="/integrations/google/callback" />} />
      <Route path="/integrations/stripe/callback" element={<UserLocaleRedirect to="/integrations/stripe/callback" />} />
      <Route path="/integrations/mercadopago/callback" element={<UserLocaleRedirect to="/integrations/mercadopago/callback" />} />
      <Route path="/integrations/outlook/callback" element={<UserLocaleRedirect to="/integrations/outlook/callback" />} />

      {/* Catch-all 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

/**
 * Routes inside /:locale/* (e.g., /en/*, /pt/*, /es/*)
 */
function LocaleRoutes() {
  const { locale } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const supportedLocales = ['en', 'pt', 'es']
  
  // Initialize language from URL locale
  const [language, setLanguage] = useState<'pt' | 'en' | 'es'>(
    (locale && supportedLocales.includes(locale) ? locale : 'en') as 'pt' | 'en' | 'es'
  )
  const { i18n } = useTranslation()

  // Sync language state with URL locale
  useEffect(() => {
    if (locale && supportedLocales.includes(locale)) {
      setLanguage(locale as 'pt' | 'en' | 'es')
      const i18nLocale = locale === 'pt' ? 'pt-BR' : locale === 'en' ? 'en' : 'es'
      i18n.changeLanguage(i18nLocale)
    }
  }, [locale, i18n])

  // Redirect to /en/ if locale not supported (after hooks)
  if (!supportedLocales.includes(locale || '')) {
    return <Navigate to="/en/" replace />
  }

  const handleNavigate = (page: string) => {
    navigate(`/${locale}/${page}`)
  }

  return (
    <Routes>
      {/* Public pages */}
      <Route path="/" element={<><LandingPage /><Toaster /></>} />
      <Route path="/login" element={<><LoginPage /><Toaster /></>} />
      <Route path="/register" element={<><RegisterPage /><Toaster /></>} />
      <Route path="/forgot-password" element={<><ForgotPasswordPage /><Toaster /></>} />
      <Route path="/reset-password/:token" element={<><ResetPasswordPage /><Toaster /></>} />
      <Route
        path="/join/:token"
        element={
          <>
            <Suspense fallback={<PageSkeleton />}>
              <JoinTeamPage />
            </Suspense>
            <Toaster />
          </>
        }
      />
      <Route path="/maintenance" element={<><MaintenancePage /><Toaster /></>} />
      <Route path="/manutencao" element={<><MaintenancePage /><Toaster /></>} />
      <Route path="/mantenimiento" element={<><MaintenancePage /><Toaster /></>} />
      <Route path="/boas-vindas" element={<><HotmartWelcomePage /><Toaster /></>} />
      <Route path="/boas-vindas/:token" element={<><HotmartWelcomePage /><Toaster /></>} />
      <Route path="/welcome" element={<WelcomeAliasRedirect />} />
      <Route path="/welcome/:token" element={<WelcomeAliasRedirect />} />

      {/* Solutions pages (SEO) */}
      <Route
        path="/solutions/:slug"
        element={
          <>
            <SolutionPage
              language={language}
              onLanguageChange={setLanguage}
            />
            <Toaster />
          </>
        }
      />

      {/* Legal pages - Privacy Policy */}
      <Route path="/privacy" element={<><PrivacyPage /><Toaster /></>} />
      <Route path="/privacidade" element={<><PrivacyPage /><Toaster /></>} />
      <Route path="/politica-de-privacidade" element={<><PrivacyPage /><Toaster /></>} />

      {/* Legal pages - Cookie Policy */}
      <Route path="/cookies" element={<><CookiePolicyPage /><Toaster /></>} />
      <Route path="/cookie-policy" element={<><CookiePolicyPage /><Toaster /></>} />
      <Route path="/politica-de-cookies" element={<><CookiePolicyPage /><Toaster /></>} />
      <Route path="/politica-cookies" element={<><CookiePolicyPage /><Toaster /></>} />

      {/* Legal pages - Terms of Service */}
      <Route path="/terms" element={<><TermsPage /><Toaster /></>} />
      <Route path="/terms-of-service" element={<><TermsPage /><Toaster /></>} />
      <Route path="/tos" element={<><TermsPage /><Toaster /></>} />
      <Route path="/termos" element={<><TermsPage /><Toaster /></>} />
      <Route path="/termos-de-servico" element={<><TermsPage /><Toaster /></>} />

      {/* Public payment routes */}
      <Route path="/pay/:publicId" element={<><PublicPaymentPage /><Toaster /></>} />
      <Route path="/pay/:publicId/balance" element={<><BalancePaymentPage /><Toaster /></>} />
      <Route path="/pay/:publicId/success" element={<><PaymentSuccessPage /><Toaster /></>} />

      {/* Public booking page (link-in-bio) */}
      <Route path="/u/:slug" element={<><PublicBookingPage /><Toaster /></>} />
      <Route path="/u/:slug/pro/:proSlug" element={<><PublicBookingPage /><Toaster /></>} />
      <Route path="/p/:slug/pro/:proSlug" element={<><PublicBookingPage /><Toaster /></>} />

      {/* Blog routes */}
      <Route 
        path="/blog" 
        element={
          <>
            <Suspense fallback={<PageSkeleton />}>
              <BlogPage language={locale as 'pt' | 'en' | 'es'} />
            </Suspense>
            <Toaster />
          </>
        } 
      />
      <Route 
        path="/blog/:slug" 
        element={
          <>
            <Suspense fallback={<PageSkeleton />}>
              <BlogPostPage language={locale as 'pt' | 'en' | 'es'} />
            </Suspense>
            <Toaster />
          </>
        } 
      />

      {/* Protected routes (app) */}
      <Route path="/*" element={<AppLayout currentPage={''} />} />
    </Routes>
  )
}

/**
 * App layout for authenticated routes
 */
function AppLayout({
  currentPage,
}: {
  currentPage?: string
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { locale } = useParams<{ locale: string }>()
  const currentLocale = locale || 'en'
  const integrationsRedirectParams = new URLSearchParams(location.search)
  integrationsRedirectParams.set('section', 'integrations')
  const integrationsRedirectSearch = integrationsRedirectParams.toString()
  const { showSuccess, upgradedPlan } = useUpgradeSuccess()
  const { updatePlan } = useAuth()

  useEffect(() => {
    if (showSuccess && upgradedPlan) {
      updatePlan(upgradedPlan as 'free' | 'starter' | 'pro' | 'studio')
      navigate(`/${currentLocale}/preferences`)
    }
  }, [showSuccess, upgradedPlan, navigate, updatePlan, currentLocale])

  // Extract current page from pathname
  const pathWithoutLocale = location.pathname.replace(/^\/(en|pt|es)/, '')
  const page = (pathWithoutLocale?.slice(1) || 'dashboard') as 'dashboard' | 'proposals' | 'follow-ups' | 'booking-requests' | 'packages' | 'contract-templates' | 'addons' | 'earnings' | 'metrics' | 'preferences' | 'pricing' | 'billing' | 'recurring-plans' | 'integrations' | 'admin' | 'team'

  return (
    <OnboardingProvider>
      <OnboardingModal />
      <AppShell 
        currentPage={page || 'dashboard'} 
        onCreateProposal={() => {
          navigate(`/${currentLocale}/proposals?create=1`)
        }}
      >
        <Routes>
        <Route path="/" element={<Navigate to={`/${currentLocale}/dashboard`} replace />} />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<DashboardSkeleton />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/proposals" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<ProposalsSkeleton />}>
                <ProposalsPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/proposals/:id" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<ProposalDetailsSkeleton />}>
                <ProposalDetailsPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/follow-ups"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageSkeleton />}>
                <FollowUpsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking-requests"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageSkeleton />}>
                <BookingRequestsInbox />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/packages"
          element={
            <ProtectedRoute>
              <Suspense fallback={<PackagesSkeleton />}>
                <PackagesPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/contract-templates" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<ContractTemplatesSkeleton />}>
                <ContractTemplatesPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/addons" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<AddOnsSkeleton />}>
                <AddOnsPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/earnings"
          element={
            <ProtectedRoute>
              <AuthorizedRoute navId="earnings">
                <Suspense fallback={<EarningsSkeleton />}>
                  <EarningsPage />
                </Suspense>
              </AuthorizedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/metrics"
          element={
            <ProtectedRoute>
              <AuthorizedRoute navId="metrics">
                <Suspense fallback={<MetricsSkeleton />}>
                  <MetricsPage />
                </Suspense>
              </AuthorizedRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/preferences" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<PreferencesSkeleton />}>
                <PreferencesPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <AuthorizedRoute navId="billing">
                <Suspense fallback={<BillingSkeleton />}>
                  <BillingPage />
                </Suspense>
              </AuthorizedRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/recurring-plans" 
          element={
            <ProtectedRoute>
              <Navigate to={`/${currentLocale}/packages`} replace />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <AuthorizedRoute navId="integrations">
                <Navigate to={`/${currentLocale}/preferences?${integrationsRedirectSearch}`} replace />
              </AuthorizedRoute>
            </ProtectedRoute>
          }
        />
        <Route 
          path="/integrations/docs" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageSkeleton />}>
                <IntegrationsDocsPage />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route
          path="/integrations/google/callback"
          element={
            <ProtectedRoute>
              <GoogleCallbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/outlook/callback"
          element={
            <ProtectedRoute>
              <OutlookCallbackPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/integrations/stripe/callback" 
          element={
            <ProtectedRoute>
              <StripeCallbackPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/integrations/mercadopago/callback"
          element={
            <ProtectedRoute>
              <MercadoPagoCallbackPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AuthorizedRoute navId="admin">
                <Suspense fallback={<PageSkeleton />}>
                  <AdminPage />
                </Suspense>
              </AuthorizedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/team"
          element={
            <ProtectedRoute>
              <AuthorizedRoute navId="team">
                <Suspense fallback={<PageSkeleton />}>
                  <TeamPage />
                </Suspense>
              </AuthorizedRoute>
            </ProtectedRoute>
          }
        />
        {/* Pricing é pública */}
        <Route path="/pricing" element={<PricingPage />} />
      </Routes>
      <Toaster />
      </AppShell>
    </OnboardingProvider>
  )
}

/**
 * 404 Page
 */
function NotFoundPage() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-8">Page not found</p>
        <Navigate to="/en/" replace />
      </div>
    </div>
  )
}

export default App
export { RootRedirect, UserLocaleRedirect }
